import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ADVISOR_DEFAULT_SLUG,
  findAdvisor,
  isSelectable,
  resolveAdvisorCopy,
} from "./advisor.data";
import type { IAdvisor } from "./types";

interface UseAdvisorArgs {
  /** "company" | "my-space" — keeps the two assistants independent. */
  scope: string;
  /** Conversation the composer is attached to, when there is one. An advisor
   *  belongs to its thread: reopening a chat restores what answered it. */
  sessionId?: string | null;
}

const scopeKey = (scope: string) => `mai.ai-advisor.${scope}`;
const sessionKey = (scope: string, sessionId: string) =>
  `mai.ai-advisor.${scope}.${sessionId}`;

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Storage blocked (private mode) — fall through to the default.
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Non-fatal: the choice still holds in memory for this session.
  }
};

/** A locked or unknown slug must never resurrect from storage. */
const resolve = (slug: string | null): string => {
  const advisor = findAdvisor(slug);
  return isSelectable(advisor) ? advisor.slug : ADVISOR_DEFAULT_SLUG;
};

const initialSlug = ({ scope, sessionId }: UseAdvisorArgs): string =>
  resolve(
    (sessionId ? read(sessionKey(scope, sessionId)) : null) ??
      read(scopeKey(scope)),
  );

/**
 * Owns the selected advisor for one assistant surface.
 *
 * Precedence: this conversation's advisor → the scope's last choice → the
 * no-web default. Web access is opt-in, so an unrecognised or admin-disabled
 * slug always resolves back to Company Only.
 */
export const useAdvisor = ({ scope, sessionId }: UseAdvisorArgs) => {
  const [slug, setSlug] = useState<string>(() =>
    initialSlug({ scope, sessionId }),
  );

  // Opening another conversation adopts that thread's advisor.
  useEffect(() => {
    setSlug(initialSlug({ scope, sessionId }));
  }, [scope, sessionId]);

  const selectAdvisor = useCallback(
    (nextSlug: string) => {
      const next = findAdvisor(nextSlug);
      if (!isSelectable(next)) return;
      setSlug(next.slug);
      write(scopeKey(scope), next.slug);
      if (sessionId) write(sessionKey(scope, sessionId), next.slug);
    },
    [scope, sessionId],
  );

  const isPersonal = scope !== "company";
  const advisor = useMemo<IAdvisor>(
    () => resolveAdvisorCopy(findAdvisor(slug), isPersonal),
    [slug, isPersonal],
  );

  return { advisor, selectAdvisor };
};
